import {
  BlotterTableContext,
  EMPTY_OBJECT,
  useAggregationsContext,
  useCurrenciesContext,
  useExposureDefinitions,
  useIntl,
  useMarketAccountsContext,
  useMarketsContext,
  useObservableValue,
  useSecuritiesContext,
  useStrategiesContext,
  useUserContext,
  type BlotterTableContextProps,
} from '@talos/kyoko';
import { useMemo, type PropsWithChildren } from 'react';
import { useCustomersByName, useCustomersContext } from '../hooks/useCustomer';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useGetCounterpartiesQuery } from './AppStateProvider/streamingDataSlice';
import { useFixingIndices } from './FixingIndexProvider';
import { useSubAccounts } from './SubAccountsContext';
import { useTradingSettings } from './TradingSettingsContext';
import { useUsersContext } from './UsersContext';

export function BlotterTableContextProvider({ children }: PropsWithChildren) {
  const { users, userByID } = useUsersContext();
  const { user: currentUser } = useUserContext();
  const intl = useIntl();

  const { strategiesByName } = useStrategiesContext();

  const { currenciesBySymbol } = useCurrenciesContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const { marketDisplayNameByName, marketTypeByName } = useMarketsContext();
  const { customers: customersObs } = useCustomersContext();
  const customers = useObservableValue(() => customersObs, [customersObs], []);
  const customersByName = useCustomersByName();
  const { marketAccountsByName, marketAccountsByID } = useMarketAccountsContext();
  const { subAccountsByName, allSubAccounts, subAccountsByID } = useSubAccounts();
  const { exposureDefinitionsByName } = useExposureDefinitions();
  const { aggregationsByName } = useAggregationsContext();
  const { data: counterpartiesByName } = useGetCounterpartiesQuery(EMPTY_OBJECT);
  const { exportDatesRaw, showAllDecimalsInBlotters } = useTradingSettings();
  const { disableBlotterErrorBanner } = useFeatureFlag();
  const { fixingIndicesByName, fixingIndicesByIndexID } = useFixingIndices();

  const context = useMemo(
    () =>
      ({
        currentUser,
        users,
        userByID,
        marketDisplayNameByName,
        marketTypeByName,
        strategiesByName,
        currenciesBySymbol,
        securitiesBySymbol,
        customers,
        customersByName,
        counterpartiesByName,
        marketAccountsByName,
        marketAccountsByID,
        subAccountsByName,
        subAccountsByID,
        allSubAccounts,
        exposureDefinitionsByName,
        aggregationsByName,
        fixingIndicesByName,
        fixingIndicesByID: fixingIndicesByIndexID,
        intl,
        settings: {
          exportDatesRaw,
          disableErrorBanner: disableBlotterErrorBanner,
          showAllDecimals: showAllDecimalsInBlotters,
        },
      }) satisfies BlotterTableContextProps,
    [
      currentUser,
      users,
      userByID,
      marketDisplayNameByName,
      marketTypeByName,
      strategiesByName,
      currenciesBySymbol,
      securitiesBySymbol,
      customers,
      customersByName,
      counterpartiesByName,
      marketAccountsByName,
      marketAccountsByID,
      subAccountsByName,
      subAccountsByID,
      allSubAccounts,
      exposureDefinitionsByName,
      aggregationsByName,
      fixingIndicesByName,
      fixingIndicesByIndexID,
      intl,
      exportDatesRaw,
      disableBlotterErrorBanner,
      showAllDecimalsInBlotters,
    ]
  );

  return <BlotterTableContext.Provider value={context}>{children}</BlotterTableContext.Provider>;
}
